import { FC, KeyboardEvent, useRef, useState } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ErrorText, Input } from '@/components';
import strings from '@/language/Swedish';
import { CoordinatesType } from '@/types/dataTypes';
import { geocodeAddress } from '@/components/map/MapHelpers';

const Container = styled.div`
  ${tw`flex flex-col gap-1`};

  .pac-target-input {
    ${tw`h-8 text-xs`};
  }
`;

interface Props {
  onPlaceSelected: (coordinates: CoordinatesType) => void;
}

const PlaceSearch: FC<Props> = ({ onPlaceSelected }) => {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [hasError, setHasError] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePlaceChanged = async () => {
    setHasError(false);

    const location = autocomplete?.getPlace()?.geometry?.location;
    if (location) {
      onPlaceSelected({ lat: location.lat(), lng: location.lng() });
      return;
    }

    // No autocomplete suggestion picked: fall back to geocoding the raw text
    const query = inputRef.current?.value;
    if (!query) return;
    try {
      const geocoded = await geocodeAddress(query);
      onPlaceSelected({ lat: geocoded.lat(), lng: geocoded.lng() });
    } catch {
      setHasError(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handlePlaceChanged();
  };

  return (
    <Container>
      <Autocomplete
        onLoad={(instance) => {
          instance.setComponentRestrictions({ country: 'SE' });
          setAutocomplete(instance);
        }}
        onPlaceChanged={handlePlaceChanged}
      >
        <Input
          type="text"
          ref={inputRef}
          placeholder={strings.indexApp.searchPlaceholder}
          onKeyDown={handleKeyDown}
        />
      </Autocomplete>
      {hasError && <ErrorText text={strings.indexApp.noPlaceFound} />}
    </Container>
  );
};

export default PlaceSearch;
